import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DataSourceRequestState } from '@progress/kendo-data-query';
import { environment } from '../../environments/environment';
import { toDataSourceQuery } from '../common/grid-filter';
import { AdminCreateTicketPayload, buildAdminCreateTicketFormData, ClinicOption } from './admin-ticket';

// Envelope every PlusDoctor API endpoint returns (see api-integration notes on work item 25961).
export interface ApiEnvelope<T> {
  Success: boolean;
  ResponseCode: number;
  Message: string;
  Data: T;
  Total: number;
  AggregateResults: unknown;
}

export interface SupportTicketDto {
  Id: number;
  TicketNumber: string;
  Subject: string;
  Description: string;
  Status: number;
  StatusName: string;
  Priority: number;
  PriorityName: string;
  ProductBrand: string | null;
  CompanyId: number | null;
  CompanyName: string | null;
  CompanyDomain: string | null;
  RaisedByUserId: string;
  RaisedByUserName: string;
  RaisedByEmail: string;
  AssignedToUserId: number | null;
  AssignedToUserName: string | null;
  ResolutionNotes: string | null;
  // JSON array of blob-storage image URLs attached when the ticket was raised, e.g.
  // '["https://.../a.png","https://.../b.png"]' — null/empty when no images were attached.
  AttachmentImageUrls: string | null;
  CreatedOn: string;
  ModifiedOn: string | null;
  ResolvedOn: string | null;
  ClosedOn: string | null;
}

export interface SupportTicketHistoryDto {
  Id: number;
  SupportTicketId: number;
  Action: number;
  ActionName: string;
  FromStatus: number | null;
  FromStatusName: string | null;
  ToStatus: number | null;
  ToStatusName: string | null;
  ToAssignedToUserId: number | null;
  ToAssignedToUserName: string | null;
  ResolutionNotes: string | null;
  Remarks: string | null;
  ActionByName: string;
  ActionByRole: 'Admin' | 'Clinic';
  ActionOn: string;
}

export interface TicketMetaOption {
  Id: number;
  Name: string;
}

export interface AssignableAgent {
  Id: number;
  Name: string;
  IsAdmin: boolean;
}

export interface SupportTicketMeta {
  StatusList: TicketMetaOption[];
  PriorityList: TicketMetaOption[];
  AssignableAgents: AssignableAgent[] | null;
}

export type SupportTicketListResponse = ApiEnvelope<SupportTicketDto[]>;
export type SupportTicketResponse = ApiEnvelope<SupportTicketDto>;
export type SupportTicketHistoryResponse = ApiEnvelope<SupportTicketHistoryDto[]>;
export type SupportTicketMetaResponse = ApiEnvelope<SupportTicketMeta>;
export type SupportTicketMutationResponse = ApiEnvelope<unknown>;

@Injectable({
  providedIn: 'root'
})
export class TicketService {

  constructor(private readonly _http: HttpClient) { }

  getAllTickets(
    state: DataSourceRequestState,
    searchText: string,
    status?: number | null,
    priority?: number | null,
    assignedToUserId?: number | null
  ): Observable<SupportTicketListResponse> {
    // Ticket dates are stored in UTC and shown in local time, so a date filter is sent as the UTC span of the local day
    const queryStr = `${toDataSourceQuery(state, { utc: true })}`;
    let url = `${environment.apiUrl}/SupportTicket/GetAllTickets?${queryStr}&searchText=${encodeURIComponent(searchText ?? '')}`;
    if (status !== null && status !== undefined) {
      url += `&status=${status}`;
    }
    if (priority !== null && priority !== undefined) {
      url += `&priority=${priority}`;
    }
    if (assignedToUserId !== null && assignedToUserId !== undefined) {
      url += `&assignedToUserId=${assignedToUserId}`;
    }
    return this._http.get<SupportTicketListResponse>(url);
  }

  getById(id: number): Observable<SupportTicketResponse> {
    return this._http.get<SupportTicketResponse>(`${environment.apiUrl}/SupportTicket/GetById?id=${id}`);
  }

  /** Raises a ticket on a clinic's behalf - multipart/form-data, see buildAdminCreateTicketFormData. */
  adminCreate(payload: AdminCreateTicketPayload): Observable<SupportTicketMutationResponse> {
    return this._http.post<SupportTicketMutationResponse>(
      `${environment.apiUrl}/SupportTicket/AdminCreate`,
      buildAdminCreateTicketFormData(payload)
    );
  }

  /** The clinics an admin can raise a ticket for - the same ones the Client list shows. */
  getClinicOptions(): Observable<ApiEnvelope<ClinicOption[]>> {
    return this._http.get<ApiEnvelope<ClinicOption[]>>(`${environment.apiUrl}/Company/GetClinicOptions`);
  }

  assign(ticketId: number, assignedToUserId: number): Observable<SupportTicketMutationResponse> {
    return this._http.put<SupportTicketMutationResponse>(`${environment.apiUrl}/SupportTicket/Assign`, {
      TicketId: ticketId,
      AssignedToUserId: assignedToUserId
    });
  }

  updateStatus(ticketId: number, status: number, resolutionNotes?: string | null): Observable<SupportTicketMutationResponse> {
    return this._http.put<SupportTicketMutationResponse>(`${environment.apiUrl}/SupportTicket/UpdateStatus`, {
      TicketId: ticketId,
      Status: status,
      ResolutionNotes: resolutionNotes ?? null
    });
  }

  close(ticketId: number, closingNote?: string | null): Observable<SupportTicketMutationResponse> {
    return this._http.put<SupportTicketMutationResponse>(`${environment.apiUrl}/SupportTicket/Close`, {
      TicketId: ticketId,
      ClosingNote: closingNote ?? null
    });
  }

  reopen(ticketId: number, reason?: string | null): Observable<SupportTicketMutationResponse> {
    return this._http.put<SupportTicketMutationResponse>(`${environment.apiUrl}/SupportTicket/Reopen`, {
      TicketId: ticketId,
      Reason: reason ?? null
    });
  }

  getHistory(ticketId: number): Observable<SupportTicketHistoryResponse> {
    return this._http.get<SupportTicketHistoryResponse>(`${environment.apiUrl}/SupportTicket/GetHistory?ticketId=${ticketId}`);
  }

  getTicketMeta(): Observable<SupportTicketMetaResponse> {
    return this._http.get<SupportTicketMetaResponse>(`${environment.apiUrl}/SupportTicket/GetTicketMeta`);
  }
}
